import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mic, MicOff, Volume2, Send, StopCircle, Sparkles, Trophy, Lightbulb, Settings2 } from "lucide-react";
import { CharacterById } from "@/components/LanguageCharacters";

function getRecognition() {
  if (typeof window === "undefined") return null;
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR ? new SR() : null;
}

function getVoicesFor(lang) {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  const all = window.speechSynthesis.getVoices() || [];
  const code = (lang || "en").toLowerCase();
  return all.filter((v) => (v.lang || "").toLowerCase().startsWith(code));
}

function speak(text, lang, voiceURI) {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang || "en-US";
    u.rate = 0.95;
    if (voiceURI) {
      const v = (window.speechSynthesis.getVoices() || []).find((vv) => vv.voiceURI === voiceURI);
      if (v) u.voice = v;
    }
    window.speechSynthesis.speak(u);
  } catch (_) {
    // noop
  }
}

export default function ConversationChat() {
  const { lang, sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text, feedback_es?, tip_es?, score?}
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [sending, setSending] = useState(false);
  const [ended, setEnded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [unsupported, setUnsupported] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState("");
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const recognitionRef = useRef(null);
  const messagesRef = useRef(null);
  const character = localStorage.getItem("lang_guide") || "owl";

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/languages/conversation/${sessionId}`);
        setSession(r.data);
        setMessages(r.data.messages || []);
        setEnded(!!r.data.ended);
        // greet via voice
        const first = (r.data.messages || []).find((m) => m.role === "assistant");
        if (first) {
          setTimeout(() => speak(first.text, langToBcp47(r.data.lang), localStorage.getItem(`lang_voice_${r.data.lang}`)), 800);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Sesión no encontrada");
        navigate(`/idiomas/${lang}`);
      } finally {
        setLoading(false);
      }
    })();
    if (!getRecognition()) setUnsupported(true);
    return () => {
      try {
        recognitionRef.current?.abort?.();
        window.speechSynthesis?.cancel?.();
      } catch (_) {}
    };
    // eslint-disable-next-line
  }, [sessionId]);

  // Load voices: speechSynthesis populates them async
  useEffect(() => {
    if (!session) return;
    const refresh = () => {
      const list = getVoicesFor(session.lang);
      setVoices(list);
      const stored = localStorage.getItem(`lang_voice_${session.lang}`);
      if (stored && list.some((v) => v.voiceURI === stored)) {
        setVoiceURI(stored);
      } else if (list.length && !voiceURI) {
        setVoiceURI(list[0].voiceURI);
      }
    };
    refresh();
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = refresh;
    }
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
    // eslint-disable-next-line
  }, [session]);

  const pickVoice = (uri) => {
    setVoiceURI(uri);
    if (session) localStorage.setItem(`lang_voice_${session.lang}`, uri);
    // sample preview
    const sample = { en: "Hello, I'll be your coach today.", fr: "Bonjour, je serai votre coach.", es: "Hola, soy tu coach hoy." }[session?.lang || "en"];
    speak(sample, langToBcp47(session?.lang), uri);
  };

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, transcript]);

  const startRecording = () => {
    if (recording) return;
    const rec = getRecognition();
    if (!rec) {
      setUnsupported(true);
      toast.error("Tu navegador no soporta grabación por voz. Usa Chrome o Edge.");
      return;
    }
    rec.lang = langToBcp47(session?.lang || lang);
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    let finalText = "";
    rec.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t + " ";
        else interim += t;
      }
      setTranscript((finalText + interim).trim());
    };
    rec.onerror = (ev) => {
      toast.error(`Micrófono: ${ev.error || "error"}`);
      setRecording(false);
    };
    rec.onend = () => {
      setRecording(false);
    };
    recognitionRef.current = rec;
    setTranscript("");
    setRecording(true);
    try {
      rec.start();
    } catch (e) {
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      recognitionRef.current?.stop?.();
    } catch (_) {}
    setRecording(false);
  };

  const sendMessage = async (text) => {
    const finalText = (text ?? transcript).trim();
    if (!finalText) {
      toast.error("Habla o escribe algo primero");
      return;
    }
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: finalText }]);
    setTranscript("");
    try {
      const r = await api.post(`/languages/conversation/${sessionId}/message`, { message: finalText });
      const ai = r.data.ai;
      setMessages((m) => [...m, ai]);
      setTimeout(() => speak(ai.text, langToBcp47(session.lang), voiceURI), 200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando");
    } finally {
      setSending(false);
    }
  };

  const endConversation = async () => {
    if (ended) return;
    setSending(true);
    try {
      const r = await api.post(`/languages/conversation/${sessionId}/end`);
      setEnded(true);
      setSummary(r.data);
      try {
        window.speechSynthesis?.cancel?.();
      } catch (_) {}
      if (r.data.xp_gained) toast.success(`+${r.data.xp_gained} XP · +${r.data.coins_gained} monedas`);
      if (r.data.global_points_awarded) toast.success(`+${r.data.global_points_awarded} pts globales`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al finalizar");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-[#374151]">Cargando conversación...</div>;
  if (!session) return null;

  const sc = session.scenario_title;

  return (
    <div className="max-w-3xl mx-auto pb-32" data-testid="conversation-chat">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(`/idiomas/conversar/${lang}`)}
          className="text-sm text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1"
          data-testid="back-to-scenarios"
        >
          <ArrowLeft className="h-4 w-4" /> Salir
        </button>
        <div className="flex items-center gap-3">
          {voices.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setVoicePickerOpen((v) => !v)}
                className="text-xs text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1"
                data-testid="open-voice-picker"
              >
                <Settings2 className="h-4 w-4" /> Voz IA
              </button>
              {voicePickerOpen && (
                <div className="absolute right-0 top-7 z-50 bg-white border border-[#E5E7EB] rounded-xl p-2 shadow-lg w-72 max-h-72 overflow-y-auto" data-testid="voice-picker">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] px-2 py-1">Elige la voz del coach</p>
                  {voices.map((v) => (
                    <button
                      key={v.voiceURI}
                      onClick={() => { pickVoice(v.voiceURI); setVoicePickerOpen(false); }}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-xs hover:bg-[#F3F4F6] ${voiceURI === v.voiceURI ? "bg-[#1B2E7A]/10 text-[#1B2E7A] font-medium" : "text-[#1A1A1A]"}`}
                      data-testid={`voice-opt-${v.voiceURI}`}
                    >
                      <span className="block truncate">{v.name}</span>
                      <span className="block text-[10px] text-[#6B7280]">{v.lang}{v.localService ? "" : " · online"}</span>
                    </button>
                  ))}
                  {voices.length === 0 && (
                    <p className="px-2 py-2 text-xs text-[#6B7280]">Tu navegador no expuso voces para este idioma.</p>
                  )}
                </div>
              )}
            </div>
          )}
          {!ended && messages.filter((m) => m.role === "user").length >= 2 && (
            <button
              onClick={endConversation}
              className="text-xs text-[#DC2626] hover:text-[#A04535] inline-flex items-center gap-1"
              data-testid="end-conversation"
            >
              <StopCircle className="h-4 w-4" /> Terminar y ver resumen
            </button>
          )}
        </div>
      </div>

      {/* Scene header */}
      <div className="bg-gradient-to-br from-[#1B2E7A] to-[#13205A] text-white rounded-2xl p-5 md:p-6 mb-6 flex items-center gap-4">
        <CharacterById id={character} size={84} mood="idle" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#F4B81F] mb-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Conversación · {labelLang(session.lang)}
          </p>
          <h1 className="font-heading text-xl md:text-2xl truncate">{sc}</h1>
          <p className="text-xs text-[#E5E7EB] mt-1">Habla con tu coach. Te responde y corrige al instante.</p>
        </div>
      </div>

      {/* Messages list */}
      <div ref={messagesRef} className="space-y-4 max-h-[480px] overflow-y-auto pr-1">
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          return (
            <MessageBubble
              key={i}
              message={m}
              sessionLang={session.lang}
              character={character}
              voiceURI={voiceURI}
              showSuggestions={isLast && m.role === "assistant" && !ended}
              onUseSuggestion={(s) => setTranscript(s)}
            />
          );
        })}
        {transcript && !sending && (
          <div className="flex justify-end">
            <div className="bg-[#1B2E7A]/10 border border-[#1B2E7A]/20 text-[#1A1A1A] rounded-2xl px-4 py-3 max-w-[80%]">
              <p className="text-xs text-[#6B7280] mb-1">Hablando...</p>
              <p className="text-sm">{transcript}</p>
            </div>
          </div>
        )}
        {sending && (
          <div className="flex items-center gap-2 text-[#6B7280] text-sm">
            <span className="inline-block h-2 w-2 rounded-full bg-[#1B2E7A] animate-pulse" />
            Tu coach está respondiendo...
          </div>
        )}
      </div>

      {/* Voice control bar */}
      {!ended && (
        <div className="fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E7EB] p-4 md:p-5">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            {unsupported ? (
              <FallbackTextInput onSend={sendMessage} disabled={sending} />
            ) : (
              <>
                <button
                  onClick={recording ? stopRecording : startRecording}
                  className={`h-14 w-14 rounded-full flex items-center justify-center text-white shadow-lg transition-all ${
                    recording
                      ? "bg-[#DC2626] animate-pulse scale-110"
                      : "bg-[#1B2E7A] hover:bg-[#13205A] hover:scale-105"
                  }`}
                  data-testid={recording ? "stop-recording" : "start-recording"}
                  aria-label={recording ? "Detener" : "Hablar"}
                >
                  {recording ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                </button>
                <div className="flex-1 min-w-0 px-3">
                  <p className="text-xs text-[#6B7280]">
                    {recording
                      ? "Grabando... habla con claridad"
                      : transcript
                      ? "Escuchaste bien. Toca enviar o vuelve a grabar."
                      : "Toca el micrófono y di tu respuesta."}
                  </p>
                  {transcript && (
                    <p className="text-sm text-[#1A1A1A] truncate" data-testid="live-transcript">
                      "{transcript}"
                    </p>
                  )}
                </div>
                <button
                  onClick={() => sendMessage()}
                  disabled={!transcript || sending}
                  className="h-12 px-5 rounded-xl bg-[#F4B81F] text-[#1A1A1A] font-medium hover:bg-[#D49538] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="send-message"
                >
                  <Send className="h-4 w-4" /> Enviar
                </button>
              </>
            )}
          </div>
          {!unsupported && (
            <p className="text-[10px] text-[#6B7280] text-center mt-2">
              ¿Sin micrófono?{" "}
              <button
                onClick={() => setUnsupported(true)}
                className="underline hover:text-[#1B2E7A]"
                data-testid="switch-to-text"
              >
                Escribe en lugar de hablar
              </button>
            </p>
          )}
        </div>
      )}

      {/* Summary modal-like end card */}
      {ended && summary && (
        <SummaryCard summary={summary} character={character} onBack={() => navigate(`/idiomas/${lang}`)} />
      )}
    </div>
  );
}

function MessageBubble({ message, sessionLang, character, voiceURI, showSuggestions, onUseSuggestion }) {
  const isUser = message.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-[#1B2E7A] text-white rounded-2xl px-4 py-3 max-w-[80%]">
          <p className="text-sm whitespace-pre-wrap">{message.text}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div className="shrink-0">
        <CharacterById id={character} size={48} />
      </div>
      <div className="flex-1 space-y-2 max-w-[85%]">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl px-4 py-3">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">Coach · en {labelLang(sessionLang)}</p>
            <button
              onClick={() => speak(message.text, langToBcp47(sessionLang), voiceURI)}
              className="text-[#6B7280] hover:text-[#1B2E7A]"
              aria-label="Escuchar"
              data-testid="replay-message"
            >
              <Volume2 className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm md:text-base text-[#1A1A1A] whitespace-pre-wrap">{message.text}</p>
        </div>
        {(message.feedback_es || message.tip_es) && (
          <div className="bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-2xl px-4 py-3 space-y-1">
            {message.feedback_es && (
              <p className="text-xs text-[#854D0E] flex items-start gap-1.5">
                <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {message.feedback_es}
              </p>
            )}
            {message.tip_es && (
              <p className="text-xs text-[#854D0E] flex items-start gap-1.5">
                <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {message.tip_es}
              </p>
            )}
            {typeof message.score === "number" && (
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] pt-1">
                Score: <span className="text-[#1A1A1A] font-medium">{message.score}/100</span>
              </p>
            )}
          </div>
        )}
        {showSuggestions && message.suggestions && message.suggestions.length > 0 && (
          <div className="space-y-1.5" data-testid="suggested-replies">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">Sugerencias para responder</p>
            <div className="flex flex-wrap gap-2">
              {message.suggestions.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => onUseSuggestion?.(s)}
                  className="text-left text-sm px-3 py-2 rounded-xl bg-white border border-[#E5E7EB] hover:border-[#1B2E7A] hover:bg-[#1B2E7A]/5 transition-all"
                  data-testid={`suggestion-${idx}`}
                  title="Toca para usar como respuesta"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FallbackTextInput({ onSend, disabled }) {
  const [text, setText] = useState("");
  const submit = () => {
    onSend(text);
    setText("");
  };
  return (
    <>
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        placeholder="Escribe tu respuesta..."
        className="flex-1 px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2E7A] focus:outline-none text-sm"
        data-testid="text-fallback-input"
      />
      <button
        onClick={submit}
        disabled={!text.trim() || disabled}
        className="h-12 px-5 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A] disabled:opacity-40"
        data-testid="text-fallback-send"
      >
        <Send className="h-4 w-4" />
      </button>
    </>
  );
}

function SummaryCard({ summary, character, onBack }) {
  const sum = summary.summary || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="conversation-summary">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl">
        <div className="flex justify-center mb-3">
          <CharacterById id={character} size={120} mood="cheer" />
        </div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] text-center">Sesión finalizada</p>
        <h2 className="font-heading text-3xl text-[#1A1A1A] text-center mb-3">¡Buen trabajo!</h2>
        {sum.summary_es && <p className="text-sm text-[#6B7280] text-center mb-4">{sum.summary_es}</p>}

        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="Turnos" value={summary.user_turns ?? 0} />
          <Stat label="Score" value={`${summary.avg_score ?? 0}/100`} />
          <Stat label="XP" value={`+${summary.xp_gained ?? 0}`} accent="#F4B81F" />
        </div>

        {summary.global_points_awarded > 0 && (
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F3F4F6] text-xs">
              ⭐ +{summary.global_points_awarded} puntos globales
            </span>
          </div>
        )}

        {sum.strengths_es?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#1B2E7A] mb-1">Fortalezas</p>
            <ul className="text-sm text-[#1A1A1A] space-y-0.5">
              {sum.strengths_es.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <Trophy className="h-3.5 w-3.5 text-[#1B2E7A] mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        {sum.to_improve_es?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#DC2626] mb-1">Mejorar</p>
            <ul className="text-sm text-[#1A1A1A] space-y-0.5">
              {sum.to_improve_es.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <Lightbulb className="h-3.5 w-3.5 text-[#DC2626] mt-0.5 shrink-0" /> {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          onClick={onBack}
          className="w-full px-6 py-3 rounded-xl bg-[#1B2E7A] text-white font-medium hover:bg-[#13205A]"
          data-testid="summary-back"
        >
          Volver al mapa
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#FFFFFF] rounded-xl p-3 text-center">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{label}</p>
      <p className="font-heading text-lg" style={{ color: accent || "#1A1A1A" }}>
        {value}
      </p>
    </div>
  );
}

function langToBcp47(code) {
  return { en: "en-US", fr: "fr-FR", es: "es-ES" }[code] || "en-US";
}

function labelLang(code) {
  return { en: "inglés", fr: "francés", es: "español" }[code] || code;
}
